import React, {createContext, useContext, useEffect} from 'react';

import {
  useViewChildrenQuery,
  useViewParentQuery,
  ViewChildrenQueryResult,
  ViewParentQueryResult,
} from '../../generated/graphql';
import {
  GALLERY_POST_VIEW_PATH,
  OLD_GALLERY_DISCUSSION_EDIT_PATH,
  OLD_GALLERY_DISCUSSION_VIEW_PATH,
} from '../../routes/paths';
import {
  DEFAULT_REPORT_PAGE_VALUES,
  ReportPageQueryData,
  ReportPageQueryResult,
  useReportPageQueryResult,
} from '../../state/graphql/reportPageQuery';
import {TeamMemberInfo, useTeamMember} from '../../state/graphql/teamMembers';
import {usePropsSelector} from '../../state/hooks';
import {useViewer} from '../../state/viewer/hooks';
import {Viewer} from '../../state/viewer/types';
import {useAdminModeActive} from '../../util/admin';
import {navigateTo} from '../../util/history';
import {accessIsPublicOrOpen} from '../../util/permissions';
import * as Urls from '../../util/urls';
import {useLoadReport, useReportMode, useReportModeSideEffects} from './hooks';
import {makeReportViewInfoSelector, ReportViewInfo} from './selectors';
import {ReportMode} from './types';

const TRUNCATED_GALLERY_PATHS = [
  GALLERY_POST_VIEW_PATH,
  OLD_GALLERY_DISCUSSION_VIEW_PATH,
  OLD_GALLERY_DISCUSSION_EDIT_PATH,
];

export type ReportPageContextType = {
  reportMode: ReportMode;
  isAdmin: boolean;
  viewer?: Viewer;
  entityName: string;
  projectName?: string;
  reportName: string;
  /**
   * The id of the report, taken from the URL path.
   * where the url: /report/<display-name>/<root-report-id>[/editdraftId=<draft-id>]
   * rootReportId is the id of the report being viewed when in view mode, and is the
   * root reportid the draft report when in edit mode.
   */
  rootReportId: string;
  reportPageQuery: ReportPageQueryResult;
  reportViewInfo: ReportViewInfo;
  isWBFeaturedReport: boolean;
  project: ReportPageQueryData['project'];
  isPublicProject: boolean;
  teamMemberInfo: TeamMemberInfo;
  reportParent: ViewParentQueryResult;
  /**
   * The id of the draft report, taken from the URL query params.
   * where the url: /report/<display-name>/<report-id>?draftId=<draft-id>
   * draftId should only be used in edit mode
   */
  draftId: string | null;
  /**
   * The draft id when a draft id is provided via /?draftId=<draft-id>, otherwise the rootReportId
   */
  viewedReportId: string;
  reportDrafts: ViewChildrenQueryResult;
};

export const ReportPageContext = createContext<
  ReportPageContextType | undefined
>(undefined);
ReportPageContext.displayName = 'ReportPageContext';

type ReportPageContextProviderProps = {
  path: string;
  entityName?: string;
  projectName?: string;
  reportId: string;
  reportName: string;
  draftId: string | null;
  isWBFeaturedReport: boolean;
  children: React.ReactNode;
};

export const ReportPageContextProvider = ({
  path,
  entityName,
  projectName,
  reportId,
  draftId,
  reportName,
  isWBFeaturedReport,
  children,
}: ReportPageContextProviderProps) => {
  const reportMode = useReportMode(path);
  const isAdmin = useAdminModeActive();
  const viewer = useViewer();
  const actualReportId = draftId ?? reportId;
  // Retrieve relevant data from redux
  useLoadReport(actualReportId);
  const reportViewInfo: ReportViewInfo = usePropsSelector(
    makeReportViewInfoSelector
  );
  const {view} = reportViewInfo;

  // Handle side effects pased on if we're editing or viewing a report
  useReportModeSideEffects({
    reportMode,
    reportViewInfo,
    entityName,
    projectName,
  });

  useEffect(() => {
    // Some older paths might not include the projectName so we go update the url with it now that we have the data
    if (view?.project != null && TRUNCATED_GALLERY_PATHS.includes(path)) {
      navigateTo({
        pathname: Urls.reportView({
          entityName: view.project.entityName,
          projectName: view.project.name,
          reportID: view.id ?? '',
          reportName: view.displayName,
        }),
        preserveSearch: true,
        preserveHash: true,
        replace: true,
      });
    }
  }, [view, path]);

  // Update url with queried report data that may have been missing
  const validEntityName: string = view?.project?.entityName ?? entityName ?? '';
  const validProjectName: string | undefined =
    view?.project?.name ?? projectName;

  const reportPageQuery = useReportPageQueryResult({
    variables: {entityName: validEntityName, projectName: validProjectName},
  });

  // edit report page used to be draftID/edit, we've chosen to
  // support draftID/edit for now, this is used to redirect to the parent report
  // when a non-author user tries to access a draft report page directly.
  const reportParent = useViewParentQuery({
    variables: {id: actualReportId},
  });

  const reportDrafts = useViewChildrenQuery({
    variables: {id: reportId},
  });

  const project =
    reportPageQuery?.data?.project ?? DEFAULT_REPORT_PAGE_VALUES.project;
  const isPublicProject =
    projectName == null ? true : accessIsPublicOrOpen(project.access);

  const teamMemberInfo = useTeamMember({
    viewer,
    entityName,
  });

  return (
    <ReportPageContext.Provider
      value={{
        reportMode,
        isAdmin,
        viewer,
        entityName: validEntityName, // entity this report is located in (team or personal entity)
        projectName: validProjectName,
        viewedReportId: actualReportId,
        reportName,
        rootReportId: reportId,
        reportPageQuery,
        reportViewInfo,
        isWBFeaturedReport,
        project,
        isPublicProject,
        teamMemberInfo,
        reportParent,
        draftId,
        reportDrafts,
      }}>
      {children}
    </ReportPageContext.Provider>
  );
};

export const useReportPageContext = (): ReportPageContextType => {
  const value = useContext(ReportPageContext);

  if (value == null) {
    throw new Error(
      'useReportPageContext must be used within a ReportPageContextProvider'
    );
  }

  return value;
};
